/**
 * One player on the roster. SPEC.md §14.1, §45, §19, §9.5, §4.5.
 *
 * A full-body character standing on a compact podium that carries their name and
 * their score, and that is the whole unit. It replaced a text chip: the scores
 * are the only thing on this screen a player looks *up* at, they are read across
 * a table rather than held at arm's length, and a face is what tells four Mikes
 * apart faster than four names at eleven pixels do. §14.1, §46
 *
 * It carries no size of its own. How big the character and the type come out is
 * decided by `.roster-grid` in index.css from three things the component cannot
 * see — how many rows the table needs, how many across each row holds, and how
 * much room the roster actually got — so this only says what the unit *is* and
 * lets the stylesheet say how big. §18.3, §45
 *
 * Three markers can be on it at once, none of them is allowed to be mistaken for
 * another, and each is drawn on exactly one thing:
 *
 *  - **The deal** is a warm outline traced around the character's silhouette,
 *    and only that. It is derived from the hand number and the seating (§9.5)
 *    and it moves when the deal does — never when somebody wins. The card around
 *    it used to carry a ring of the same colour; two marks for one fact is a
 *    heavier voice than the fact deserves, and the fact is only whose turn it is
 *    to shuffle. §8.3, §19
 *  - **The hand's winner** is the crown and the tinted band: whoever is
 *    currently silent, read out of everybody else's reports rather than
 *    designated. It is about the *live* hand and goes when that hand does. §3.1
 *  - **The celebration** is the dance, and the dance alone. What dances is
 *    decided by `winnerCelebration` in shared from the ledger — not by the crown
 *    beside it, not by the outline, not by this device's own seat. It used to
 *    tint the band as well, for the thirty seconds it runs; the dance already
 *    says who won, and saying it a second time in green recoloured the one box
 *    on this screen that carries a score. §4.5, §60
 */

import { useMemo, type CSSProperties } from 'react';
import {
  CELEBRATION_DANCE_CYCLE_MS,
  describeScore,
  formatSigned,
  truncateName,
  type RosterSlot,
} from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { useIsDealer, useTotalFor } from '@/stores/session-store';
import { useCelebration } from '@/stores/ui-store';

export interface PlayerPodiumProps {
  playerId: string;
  name: string;
  avatarKey: string;
  /** They have reported a loss, so they are not the one this hand is going to. */
  reported: boolean;
  /** The hand is going to them: they are the only player who said nothing. §3.1 */
  isWinner: boolean;
  /** Where the shared `rosterLayout` put them. §45 */
  slot: RosterSlot;
  /** How long a name may be before it is cut, which is a function of the row. §8.6 */
  nameMax: number;
}

export function PlayerPodium({
  playerId,
  name,
  avatarKey,
  reported,
  isWinner,
  slot,
  nameMax,
}: PlayerPodiumProps) {
  const total = useTotalFor(playerId);
  const isDealer = useIsDealer(playerId);
  const celebration = useCelebration();

  /**
   * The cheer, and it is now the *whole* of how a won hand is marked. §4.5
   *
   * The band used to tint green for the length of the celebration as well. It
   * was one signal too many: the dance already says who won, it says it for
   * thirty seconds, and it says it without recolouring the one box on this screen
   * that carries a score. The same green still marks whoever the *live* hand is
   * going to (§3.1) — which is a different fact about a different hand, and the
   * only one left that turns a band green.
   */
  const dancing = celebration !== null && celebration.playerId === playerId;
  const danceId = dancing ? celebration.id : null;
  const danceCycles = dancing ? celebration.cycles : 0;
  const danceStartedAt = dancing ? celebration.startedAt : 0;

  /**
   * Computed once per celebration rather than on every render, and that is
   * load-bearing twice over.
   *
   * A negative delay starts the animation already part-way through, which is how
   * a remount rejoins a celebration instead of restarting it — the store's timer
   * counts from `startedAt` and does not restart, so a dance that began again
   * from zero would be cut off partway through. But this podium re-renders on
   * every score, every report and every reconnect, and recomputing the delay on
   * each of those would wind the animation forward by however long it had been
   * running: five seconds in, a re-render would jump it to ten. So the elapsed
   * time is read when the celebration *changes* and not again. §4.5
   */
  const danceStyle = useMemo<CSSProperties | undefined>(() => {
    if (danceId === null) return undefined;
    return {
      animationDuration: `${String(CELEBRATION_DANCE_CYCLE_MS)}ms`,
      animationIterationCount: danceCycles,
      animationDelay: `-${String(Math.max(0, Date.now() - danceStartedAt))}ms`,
    };
    // The identity of the thing being cheered, which is what a new celebration
    // changes and a re-render does not.
  }, [danceId, danceCycles, danceStartedAt]);

  const tone =
    total > 0 ? 'text-panel-positive' : total < 0 ? 'text-panel-negative' : 'text-panel-muted';

  const style: CSSProperties = {
    gridRow: slot.row,
    // Two tracks per unit: see `rosterLayout`, which is what centres a short row.
    gridColumn: `${String(slot.columnStart)} / span 2`,
  };

  return (
    <li className="roster-unit" style={style}>
      {/*
        The card carries no marker of its own. The deal is drawn on the character
        — an outline traced around the silhouette itself (§9.5) — and a ring
        around the box as well was the same fact said twice, in a heavier voice
        than the fact deserves: it is whose turn it is to shuffle, not something
        anybody has to act on. §8.3, §19
      */}
      <div className="roster-card">
        <span className="roster-art">
          <Avatar
            /*
              Keyed on the celebration so a *new* one actually restarts the dance.
              Without it, the same player winning two hands running keeps an element
              whose animation has already run its thirty cycles: the class is still
              there, the delay is recomputed, and the character stands perfectly
              still through their own celebration. §4.5
            */
            key={danceId ?? 'still'}
            avatarKey={avatarKey}
            className={[
              'roster-avatar',
              isDealer ? 'is-dealer' : '',
              dancing ? 'celebrating-avatar' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={danceStyle}
          />

          {/* The crown goes to whoever the hand is currently going to — which is
              read out of everyone else's reports, not designated. §3.1, §8.3 */}
          {isWinner ? (
            <span aria-hidden="true" className="roster-crown">
              👑
            </span>
          ) : null}
        </span>

        <span
          className={[
            'roster-podium',
            // Whoever the *live* hand is going to, and nothing else: never the
            // dealer's, never a drinker's, and no longer the winner of a hand
            // that has already ended — the dance is what says that. §3.1, §9.5, §4.5
            isWinner ? 'is-highlit' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span aria-hidden="true" className="roster-name">
            {truncateName(name, nameMax)}
          </span>

          <span aria-hidden="true" data-numeric className={`roster-score ${tone}`}>
            {formatSigned(total)}
          </span>
        </span>

        {/* Reported is a dot rather than a word: the band is already a name and a
            figure at ten pixels, and the crown above is what actually matters.
            On the card's corner rather than the band's — the band has no spare
            corner left — and absolutely placed, so it costs the type no room and
            a report landing mid-hand reflows nothing. §20.2, §20.4 */}
        <span aria-hidden="true" className={`roster-reported ${reported ? '' : 'is-quiet'}`} />

        {/* The whole unit, spoken once, in the one form every surface shares. §19 */}
        <span className="sr-only">
          {name}, {describeScore(total)}
          {isWinner ? ', winning this hand' : reported ? ', reported' : ', yet to report'}
          {isDealer ? ', dealing this hand' : ''}
        </span>
      </div>
    </li>
  );
}
